import React, { useState } from 'react';
import BirthdayField from '../custom-form/BirthdayField';
import { parseBirthday, type BirthdayParts } from '../custom-form/birthday';
import { getLocalToday } from '../../utils/date';

// Identity step-up before the forms render (backend#form-verify). A form link is a
// bearer credential, so the patient confirms the pinned chart's date of birth before
// the forms (and the usable token) are released — the possession factor (the SMS
// arrived on their phone) plus a knowledge factor. First-name-less by design: we ask,
// we don't reveal, so a wrong recipient learns nothing.

interface Props {
  practiceName: string;
  formsCount: number;
  verifying: boolean;
  /** Set on a wrong DOB (401) or lockout (429); cleared on the next edit. */
  error: string | null;
  onVerify: (dob: string) => void;
  onEdit: () => void;
}

export function V2VerifyStep({ practiceName, formsCount, verifying, error, onVerify, onEdit }: Props) {
  const [parts, setParts] = useState<BirthdayParts>({ month: '', day: '', year: '' });
  const [localError, setLocalError] = useState<string | null>(null);
  const activeError = localError ?? error;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (verifying) return;
    const result = parseBirthday(parts, getLocalToday());
    if (result.status !== 'valid') {
      const messages = {
        empty: '"Date of birth" is required.',
        partial: 'Enter the month, day and year for "Date of birth".',
        invalid: '"Date of birth" must be a real date.',
        future: `"Date of birth" can't be in the future.`,
      };
      setLocalError(messages[result.status]);
      return;
    }
    onVerify(result.iso);
  }

  return (
    <div className="py-4">
      <div className="text-center max-w-md mx-auto">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
          Verify your identity
        </h2>
        <p className="text-gray-500 mt-3">
          {practiceName} sent you {formsCount === 1 ? 'a form' : `${formsCount} forms`} to
          complete. To protect your health information, please confirm the patient&apos;s
          date of birth to continue.
        </p>
      </div>

      <form
        className="mt-8 space-y-4 max-w-md mx-auto"
        onSubmit={submit}
      >
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 px-5 py-5">
          <BirthdayField
            field={{ label: 'Date of birth', required: false }}
            inputId="verify-dob"
            parts={parts}
            onPartChange={(part, value) => {
              setParts((current) => ({ ...current, [part]: value }));
              setLocalError(null);
              onEdit();
            }}
            disabled={verifying}
            errorId={activeError ? 'verify-dob-error' : undefined}
            className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 focus:border-primary-deep focus:ring-1 focus:ring-primary-deep outline-none"
          />
          {activeError && (
            <p id="verify-dob-error" role="alert" className="mt-3 text-sm font-medium text-red-600">{activeError}</p>
          )}
        </div>

        <button
          type="submit"
          data-testid="verify-submit"
          disabled={verifying}
          className="btn-primary w-full bg-primary-deep hover:bg-primary-deeper disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {verifying ? 'Verifying…' : 'Continue'}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Your answers will be encrypted and sent securely to the office.
        </p>
      </form>
    </div>
  );
}
